import React, { useState, useEffect } from 'react';
import { BottomSheet, StatusActionButton, WorkTypeSelector } from '../../../shared/ui';
import { questionsV2Api, CreateQuestionV2Request } from '../../../api/questionsV2Api';
import { useAuth } from '../../../shared/contexts/AuthContext';
import UserSelector from './UserSelector';
import './CreateQuestionV2Modal.css';
import { questionMemberAddPermissionsApi } from '../../../api/questionMemberAddPermissionsApi';

interface CreateQuestionV2ModalProps {
  isOpen: boolean;
  siteId: number;
  typeId?: number; // 1=Question, 2=Note, 3=Supply
  onClose: () => void;
  onSuccess: () => void;
}

const CreateQuestionV2Modal: React.FC<CreateQuestionV2ModalProps> = ({
  isOpen,
  siteId,
  typeId = 1, // По умолчанию Question
  onClose,
  onSuccess
}) => {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [selectedTypeOfWorksId, setSelectedTypeOfWorksId] = useState<number | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addableRoleIds, setAddableRoleIds] = useState<number[] | null>(null);

  // Загрузка прав на добавление
  useEffect(() => {
    if (isOpen) {
      loadAddableRoleIds();
    }
  }, [isOpen]);

  const loadAddableRoleIds = async () => {
    try {
      const roleIds = await questionMemberAddPermissionsApi.getAddableRoleIds(siteId, typeId);
      setAddableRoleIds(roleIds);
    } catch (err) {
      console.error('Error loading addable role IDs:', err);
      // Не блокируем UI, но выводим ошибку в консоль
    }
  };

  const getModalTitle = () => {
    switch (typeId) {
      case 1:
        return 'Создать дискуссию';
      case 2:
        return 'Создать заметку';
      case 3:
        return 'Создать заявку на снабжение';
      default:
        return 'Создать дискуссию';
    }
  };

  const getContentTypeName = () => {
    switch (typeId) {
      case 1:
        return 'дискуссию';
      case 2:
        return 'заметку';
      case 3:
        return 'заявку';
      default:
        return 'дискуссию';
    }
  };

  const getContentTypeNameGenitive = () => {
    switch (typeId) {
      case 1:
        return 'дискуссии';
      case 2:
        return 'заметки';
      case 3:
        return 'заявки';
      default:
        return 'дискуссии';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!text.trim()) {
      setError(`Текст ${getContentTypeNameGenitive()} не может быть пустым`);
      return;
    }
    
    if (!selectedTypeOfWorksId) {
      setError('Выберите тип работы');
      return;
    }

    if (!user) {
      setError('Пользователь не авторизован');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const questionData: CreateQuestionV2Request = {
        userId: user!.id,
        siteId: siteId,
        typeOfWorkId: selectedTypeOfWorksId,
        typeId: typeId, // 1 = Question, 2 = Note, 3 = Supply
        text: text.trim(),
        closed: false,
        visibilityType: 1 // 1 = private, 2 = public
      };

      const result = await questionsV2Api.createQuestion(siteId, questionData);
      
      // Если выбраны участники, добавляем их
      if (selectedUserIds.length > 0) {
        for (const userId of selectedUserIds) {
          await questionsV2Api.addQuestionMember(siteId, result.id, userId);
        }
      }

      // Сброс формы
      setText('');
      setSelectedTypeOfWorksId(null);
      setSelectedUserIds([]);
      
      onSuccess();
    } catch (err: any) {
      console.error('Error creating question:', err);
      
      // Проверяем, есть ли сообщение об ошибке от сервера
      if (err?.response?.status === 403) {
        const errorMessage = err?.response?.data?.error || 'У вас нет прав на создание дискуссий';
        setError(errorMessage);
      } else if (err?.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Ошибка создания дискуссии');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setText('');
    setSelectedTypeOfWorksId(null);
    setSelectedUserIds([]);
    setError(null);
    onClose();
  };

  const handleParticipantSelectionChange = (userIds: number[]) => {
    setSelectedUserIds(userIds);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      title={getModalTitle()}
      size="large"
      showHandle={true}
      allowSwipeToClose={true}
      allowSwipeToExpand={true}
    >
      <form onSubmit={handleSubmit} className="create-question-form">
        {error && (
          <div className="error-message">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        {/* Выбор типа работы */}
        <WorkTypeSelector
          value={selectedTypeOfWorksId}
          onChange={setSelectedTypeOfWorksId}
          placeholder="Выберите тип работы"
          required
        />

        {/* Текст вопроса */}
        <div className="form-group">
          <textarea
            id="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="form-textarea"
            placeholder={`Опишите вашу ${getContentTypeName()}...`}
            rows={4}
            required
          />
        </div>

        {/* Выбор участников */}
        <div className="form-group">
          <UserSelector
            siteId={siteId}
            typeId={typeId}
            selectedUserIds={selectedUserIds}
            onSelectionChange={handleParticipantSelectionChange}
            addableRoleIds={addableRoleIds}
          />
        </div>

        {/* Кнопки */}
        <div className="form-actions">
          <StatusActionButton
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Отмена
          </StatusActionButton>
          <StatusActionButton
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading || !text.trim() || !selectedTypeOfWorksId}
            icon="ri-add-line"
          >
            Создать {getContentTypeName()}
          </StatusActionButton>
        </div>
      </form>
    </BottomSheet>
  );
};

export default CreateQuestionV2Modal;
